import { type ActionEvent, Controller } from "@hotwired/stimulus"

import { triggerGeolocationSearch } from "@/components/geolocation"
import { renderTurboStream } from "@/helpers/turbo"
import { debounceAsync, hasFocusedElement, setInputValueWithEvents } from "@/helpers/utils"
import ModalController from "../modal_controller"

interface Location {
  locationType: "" | "geolocation" | "place" | "region" | "venue"
  maxDistance: string
  name: string
  slug: string
}

export default class extends Controller {
  static outlets = ["geolocation-modal"]
  declare geolocationModalOutlet: ModalController

  static targets = [
    "defaultResultsList",
    "hiddenInputLocationType",
    "hiddenInputMaxDistance",
    "hiddenInputSlug",
    "input",
    "popularDestinationsList",
    "recentLocationsList",
    "result",
    "resetButton",
    "spasNearMe",
    "skipButton",
    "turboStreamResultsList"
  ]
  declare readonly defaultResultsListTargets: HTMLOListElement[]
  declare readonly hiddenInputLocationTypeTarget: HTMLInputElement
  declare readonly hiddenInputMaxDistanceTarget: HTMLInputElement
  declare readonly hiddenInputSlugTarget: HTMLInputElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly popularDestinationsListTarget: HTMLOListElement
  declare readonly recentLocationsListTarget: HTMLOListElement
  declare readonly resultTargets: HTMLElement[]
  declare readonly resetButtonTarget: HTMLButtonElement
  declare readonly hasResetButtonTarget: boolean
  declare readonly spasNearMeTarget: HTMLOListElement
  declare readonly skipButtonTarget: HTMLButtonElement
  declare readonly hasSkipButtonTarget: boolean
  declare readonly turboStreamResultsListTarget: HTMLOListElement

  static values = { endpoint: String, resultsId: String, selectedLocation: Object }
  declare readonly endpointValue: string
  declare readonly resultsIdValue: string
  declare selectedLocationValue: Location

  private lookupAbort = new window.AbortController()

  initialize(): void {
    this.lookup = debounceAsync(this.lookup.bind(this), 250)
  }

  connect(): void {
    // Input disabled by default until controller loaded.
    this.inputTarget.disabled = false
  }

  clearLocation(): void {
    setInputValueWithEvents(this.inputTarget, "")
  }

  selectedLocationValueChanged(): void {
    // Set value with `change` only, because `input` would trigger another lookup fetch.
    setInputValueWithEvents(this.inputTarget, this.selectedLocationValue.name, ["change"])

    this.hiddenInputLocationTypeTarget.value = this.selectedLocationValue.locationType
    this.hiddenInputMaxDistanceTarget.value = this.selectedLocationValue.maxDistance
    this.hiddenInputSlugTarget.value = this.selectedLocationValue.slug

    // Only send max distance when it has a value.
    this.hiddenInputMaxDistanceTarget.disabled = !this.selectedLocationValue.maxDistance
    this.updateSkipAndResetButtons()
  }

  async lookup({ target }: ActionEvent): Promise<void> {
    const input = target as HTMLInputElement
    const query = input.value.trim()

    this.selectedLocationValue = { ...this.emptyLocation, name: input.value }

    if (query) {
      await this.renderResults(query)
      this.defaultResultsListTargets.forEach((target) => (target.hidden = true))
      this.spasNearMeTarget.hidden = true
    } else {
      this.turboStreamResultsListTarget.innerHTML = ""
      this.spasNearMeTarget.hidden = false

      if (this.isModal) {
        this.popularDestinationsListTarget.hidden = document.activeElement !== this.inputTarget
        this.recentLocationsListTarget.hidden = document.activeElement === this.inputTarget
      } else {
        this.defaultResultsListTargets.forEach((target) => (target.hidden = false))
      }
    }
  }

  onFocus(): void {
    this.inputTarget.select()

    if (this.isModal) {
      this.popularDestinationsListTarget.hidden =
        !!this.turboStreamResultsListTarget.children.length
      this.recentLocationsListTarget.hidden = true
    }
  }

  onBlur(): void {
    if (this.isModal) {
      this.popularDestinationsListTarget.hidden = true
      this.recentLocationsListTarget.hidden = !!this.turboStreamResultsListTarget.children.length
    }
  }

  // Keep input focused so click on a result registers before blur
  onResultMousedown(e: Event): void {
    e.preventDefault()
  }

  selectFirstResultIfBlurred({ relatedTarget }: FocusEvent): void {
    const isLocationSelected = this.hiddenInputSlugTarget.value
    const isInputBlank = !this.inputTarget.value

    // Do nothing if element still has focus, location already selected, or input blank (because
    // empty location is valid).
    if (hasFocusedElement(this.element, relatedTarget) || isLocationSelected || isInputBlank) {
      return
    }

    const firstVisibleResult = this.resultTargets.find((target) => !target.hidden)

    // Select first result if there are visible results to select from.
    if (firstVisibleResult) {
      firstVisibleResult.click()
    } else {
      this.turboStreamResultsListTarget.innerHTML = ""
      this.defaultResultsListTargets.forEach((target) => (target.hidden = false))
      this.spasNearMeTarget.hidden = false
      this.selectedLocationValue = this.emptyLocation
    }
  }

  async selectResult({ params: { result } }: ActionEvent): Promise<void> {
    if (result.locationType === "geolocation") {
      try {
        const coords = await triggerGeolocationSearch()
        if (coords) {
          this.selectedLocationValue = { ...result, slug: `${coords.lat},${coords.lng}` }
        } else {
          this.selectedLocationValue = this.emptyLocation
        }

        this.inputTarget.blur()
      } catch {
        this.geolocationModalOutlet.open()
      }
    } else {
      this.selectedLocationValue = result
    }
  }

  updateSkipAndResetButtons(): void {
    if (this.hasResetButtonTarget && this.hasSkipButtonTarget) {
      const isSelected = !!this.inputTarget.value

      this.resetButtonTarget.hidden = !isSelected
      this.skipButtonTarget.hidden = isSelected
    }
  }

  private get emptyLocation(): Location {
    return {
      locationType: "",
      maxDistance: "",
      name: "",
      slug: ""
    }
  }

  private async renderResults(query: string): Promise<void> {
    const url = new URL(this.endpointValue, window.location.origin)
    const params = new URLSearchParams({
      q: query,
      results_id: this.resultsIdValue
    })
    url.search = params.toString()

    // Abort any previous query and reset controller.
    this.lookupAbort.abort()
    this.lookupAbort = new window.AbortController()

    await renderTurboStream(url.toString(), { signal: this.lookupAbort.signal })
  }

  private get isModal(): boolean {
    return this.hasSkipButtonTarget
  }
}
